import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ClipboardList, Plus } from 'lucide-react';
import { taskRequestApi } from '../services/taskRequestApi';
import { useDialog } from '@hooks/useDialog';
import TaskFilters from '../components/TaskFilters';
import TaskList from '../components/TaskList';
import CreateTaskDialog from '../components/CreateTaskDialog';
import type { TaskRequestFilters } from '../types/taskRequest';
import RequirePermission from '@shared/components/RequirePermission';
import './TasksPage.css';

export default function TasksPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const createDialog = useDialog();
  const [filters, setFilters] = useState<TaskRequestFilters>({
    page: 1,
    pageSize: 20,
    sortColumn: 'createdAt',
    sortDirection: 'DESC',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['task-requests', filters],
    queryFn: () => taskRequestApi.getTaskRequests(filters),
  });

  const { data: counts } = useQuery({
    queryKey: ['task-request-counts'],
    queryFn: () => taskRequestApi.getTaskRequestCounts(),
    refetchInterval: 60_000,
  });

  const pendingCount =
    (counts ?? []).reduce((sum, c) => {
      if (c.status === 'New' || c.status === 'InProgress') return sum + c.count;
      return sum;
    }, 0);

  const handleFiltersChange = useCallback((newFilters: TaskRequestFilters) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleTaskCreated = useCallback((taskId: number) => {
    createDialog.close();
    queryClient.invalidateQueries({ queryKey: ['task-requests'] });
    queryClient.invalidateQueries({ queryKey: ['task-request-counts'] });
    navigate(`/tasks/${taskId}`);
  }, [createDialog, queryClient, navigate]);

  return (
    <div className="tasks-page">
      <div className="tasks-page-header">
        <div className="page-title-row">
          <ClipboardList size={28} />
          <h1>Task Requests</h1>
          {pendingCount > 0 && (
            <span className="tasks-pending-badge">{pendingCount}</span>
          )}
          <RequirePermission permission="UserCanCreateClinicTaskRequest">
            <button
              type="button"
              className="task-action-btn task-action-btn-primary tasks-page-create-btn"
              onClick={createDialog.open}
            >
              <Plus size={16} className="task-btn-icon" />
              Create Task
            </button>
          </RequirePermission>
        </div>
        <p className="page-subtitle">
          Manage task requests for clinic setup and relationship creation.
        </p>
      </div>

      <TaskFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <TaskList
        tasks={data?.items ?? []}
        totalCount={data?.totalCount ?? 0}
        page={filters.page}
        pageSize={filters.pageSize}
        isLoading={isLoading}
        onPageChange={handlePageChange}
      />

      {createDialog.isOpen && (
        <CreateTaskDialog
          onCreated={handleTaskCreated}
          onCancel={createDialog.close}
        />
      )}
    </div>
  );
}
